import { t as globalT } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { Container, Grid, Stack } from "@mantine/core";
import { useSuspenseQueryPostgrest } from "@sweco-se/reality-frontend";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useMemo, useRef } from "react";
import { useAuth } from "react-oidc-context";
import z from "zod";

import { BACKEND_URL, api } from "@/api.ts";
import AuthorizationConfirmButton from "@/components/authorization/AuthorizationConfirmButton";
import AuthorizationDatasetSelector from "@/components/authorization/AuthorizationDatasetSelector.tsx";
import AuthorizationResponsibilityBoxes from "@/components/authorization/AuthorizationResponsibilityBoxes.tsx";
import AuthorizationTermsAndConditions from "@/components/authorization/AuthorizationTermsAndConditions.tsx";
import {
  AuthorizationContext,
  useAuthorizeController,
} from "@/hooks/useAuthorizeController.tsx";

export const Route = createFileRoute(
  "/flows/authorize-external/authorize/$identifier",
)({
  component: RouteComponent,
  head: () => ({
    meta: [
      {
        title: globalT`Sweco REALity` + " - " + globalT`Authorize`,
      },
    ],
  }),
  validateSearch: z
    .object({
      client_id: z.string().min(1, "client_id must not be empty"),
    })
    .catchall(z.any()),
});

function RouteComponent() {
  const search = Route.useSearch();
  const client = useSuspenseQueryPostgrest(
    useMemo(
      () =>
        api
          .from("client")
          .select("client_datasets,permitted_datasets")
          .eq("id", search.client_id as unknown as number)
          .single()
          .throwOnError(),
      [search.client_id],
    ),
  );

  const authorization = useAuthorizeController(Route.useParams().identifier, [
    ...client.data.client_datasets,
    ...client.data.permitted_datasets,
  ]);

  const postFormRef = useRef<HTMLFormElement>(null);
  const onConfirm = useCallback(async () => {
    await authorization.insertAdded();
    postFormRef.current?.submit();
  }, [authorization]);

  const auth = useAuth();

  const { t } = useLingui();

  return (
    <AuthorizationContext.Provider value={authorization}>
      <Container size="lg">
        <Grid>
          <Grid.Col span={12}>
            <Stack>
              <p style={{ textAlign: "center", marginBottom: 0 }}>
                <Trans>
                  Please review and select the datasets you want access to
                </Trans>
              </p>
              <strong style={{ textAlign: "center" }}>
                {authorization.project?.identifier}{" "}
                {authorization.project?.name}
              </strong>
            </Stack>
          </Grid.Col>
          <Grid.Col span={12}>
            <h3>
              <Trans>Terms and conditions</Trans>
            </h3>
            <AuthorizationTermsAndConditions />
          </Grid.Col>
          <Grid.Col span={4}>
            <h3>
              1. <Trans>Datasets</Trans>
            </h3>
            <AuthorizationDatasetSelector />
          </Grid.Col>
          <Grid.Col span={4}>
            <h3>
              2. <Trans>Responsibilities</Trans>
            </h3>
            <AuthorizationResponsibilityBoxes expectNewSelection />
          </Grid.Col>
          <Grid.Col span={4}>
            <h3>
              3. <Trans>Costs</Trans>
            </h3>
            <Stack gap="lg">
              <p>
                <Trans>
                  Currently, during the testing phase, there are no costs
                  associated with using this service
                </Trans>
              </p>
            </Stack>
          </Grid.Col>
          <Grid.Col span={4} offset={4}>
            <AuthorizationConfirmButton
              onConfirm={onConfirm}
              text={t`Continue`}
            />
          </Grid.Col>

          <form
            method="post"
            action={`${BACKEND_URL}/oauth2/authorize?project=${authorization.project?.identifier}&${new URLSearchParams(search).toString()}`}
            ref={postFormRef}
          >
            <input
              type="hidden"
              name="access_token"
              value={auth.user?.access_token}
            />
          </form>
        </Grid>
      </Container>
    </AuthorizationContext.Provider>
  );
}
